import React, { useState, useEffect } from 'react';
import { X, Search, Download, AlertCircle, CheckCircle, Loader2, Swords, Settings } from 'lucide-react';
import { userSettingsDB } from '../lib/supabase';
import { getAccountByRiotId, getMatchIds, getMatchDetails, getMatchTimeline, parseMatchToApexGame } from '../lib/riot';
import { getChampionIconUrl } from '../lib/champions';
import Modal from '../design-system/Modal';
import Button from '../design-system/Button';

const REGIONS = [
  { value: 'euw1', label: 'EUW', platform: 'europe' },
  { value: 'eun1', label: 'EUNE', platform: 'europe' },
  { value: 'na1', label: 'NA', platform: 'americas' },
  { value: 'kr', label: 'KR', platform: 'asia' },
  { value: 'br1', label: 'BR', platform: 'americas' },
  { value: 'la1', label: 'LAN', platform: 'americas' },
  { value: 'la2', label: 'LAS', platform: 'americas' },
  { value: 'tr1', label: 'TR', platform: 'europe' },
  { value: 'oc1', label: 'OCE', platform: 'sea' },
  { value: 'jp1', label: 'JP', platform: 'asia' },
];

export default function RiotImportModal({ onClose, onImport, existingGames, champions, version }) {
  const [step, setStep] = useState('loading'); // loading | config | matches | importing
  const [settings, setSettings] = useState(null);
  const [gameName, setGameName] = useState('');
  const [tagLine, setTagLine] = useState('EUW');
  const [region, setRegion] = useState('euw1');
  const [matches, setMatches] = useState([]);
  const [loadingMatches, setLoadingMatches] = useState(false);
  const [error, setError] = useState(null);
  const [importingIds, setImportingIds] = useState(new Set());

  const selectedRegion = REGIONS.find(r => r.value === region) || REGIONS[0];
  const existingMatchIds = new Set(existingGames.map(g => g.riot_match_id).filter(Boolean));

  // Load settings on mount
  useEffect(() => {
    (async () => {
      try {
        const s = await userSettingsDB.get();
        if (s?.riot_puuid) {
          setSettings(s);
          setGameName(s.riot_game_name || '');
          setTagLine(s.riot_tag_line || 'EUW');
          setRegion(s.riot_region || 'euw1');
          setStep('matches');
          loadMatches(s);
        } else {
          setStep('config');
        }
      } catch (e) {
        setError(e.message);
        setStep('config');
      }
    })();
  }, []);

  const saveSettingsAndLoad = async () => {
    setError(null);
    if (!gameName || !tagLine) {
      setError('Remplis ton Riot ID (gameName et tagLine)');
      return;
    }
    setLoadingMatches(true);
    try {
      // Fetch PUUID
      const account = await getAccountByRiotId(gameName, tagLine, selectedRegion.platform);
      const puuid = account.puuid;

      // Save settings
      const saved = await userSettingsDB.upsert({
        riot_game_name: gameName,
        riot_tag_line: tagLine,
        riot_puuid: puuid,
        riot_region: region,
        riot_platform: selectedRegion.platform
      });
      setSettings(saved);

      // Load matches
      await loadMatches(saved);
      setStep('matches');
    } catch (e) {
      if (e.message?.includes('not configured')) {
        setError('⚠️ La clé API Riot n\'est pas encore configurée côté serveur. Voir le guide Phase 2 dans le README.');
      } else if (e.message?.includes('404')) {
        setError('❌ Riot ID introuvable. Vérifie gameName#tagLine et la région.');
      } else if (e.message?.includes('403')) {
        setError('🔑 Clé API invalide ou expirée. Si tu as une Development Key, elle expire toutes les 24h.');
      } else {
        setError('Erreur : ' + e.message);
      }
    } finally {
      setLoadingMatches(false);
    }
  };

  const loadMatches = async (s) => {
    setLoadingMatches(true);
    setError(null);
    try {
      const matchIds = await getMatchIds(s.riot_puuid, {
        count: 20,
        queue: 420, // Solo/duo ranked
        platform: s.riot_platform || 'europe'
      });

      // Récupérer les détails en parallèle (mais limité pour respecter rate limits)
      const details = [];
      for (let i = 0; i < matchIds.length; i += 5) {
        const batch = matchIds.slice(i, i + 5);
        const results = await Promise.all(
          batch.map(id => getMatchDetails(id, s.riot_platform || 'europe').catch(e => null))
        );
        details.push(...results.filter(Boolean));
      }

      // Mapper vers un format compact pour affichage
      const compactMatches = details.map(m => {
        const me = m.info.participants.find(p => p.puuid === s.riot_puuid);
        if (!me) return null;
        return {
          matchId: m.metadata.matchId,
          champion: me.championName,
          result: me.win ? 'W' : 'L',
          kills: me.kills,
          deaths: me.deaths,
          assists: me.assists,
          duration: Math.round(m.info.gameDuration / 60),
          timestamp: m.info.gameCreation,
          fullMatch: m,
          alreadyImported: existingMatchIds.has(m.metadata.matchId)
        };
      }).filter(Boolean);

      setMatches(compactMatches);
    } catch (e) {
      if (e.message?.includes('not configured')) {
        setError('⚠️ La clé API Riot n\'est pas encore configurée côté serveur.');
      } else {
        setError('Erreur : ' + e.message);
      }
    } finally {
      setLoadingMatches(false);
    }
  };

  const importMatch = async (match) => {
    if (!settings) return;
    setImportingIds(prev => new Set(prev).add(match.matchId));
    try {
      // Re-fetch timeline pour avoir les morts
      const timeline = await getMatchTimeline(match.matchId, settings.riot_platform || 'europe');
      const apexGame = parseMatchToApexGame(match.fullMatch, timeline, settings.riot_puuid);
      await onImport(apexGame);
      // Marquer comme importé
      setMatches(ms => ms.map(m => m.matchId === match.matchId ? { ...m, alreadyImported: true } : m));
    } catch (e) {
      alert('Erreur d\'import : ' + e.message);
    } finally {
      setImportingIds(prev => {
        const next = new Set(prev);
        next.delete(match.matchId);
        return next;
      });
    }
  };

  const resetConfig = () => {
    if (!confirm('Réinitialiser la configuration Riot ? Tu devras ressaisir ton Riot ID.')) return;
    setStep('config');
    setSettings(null);
    setMatches([]);
  };

  return (
    <Modal open onClose={onClose} title='Charger mes games automatiquement' subtitle='' size="xl">
      <div className="space-y-4">
          {step === 'loading' && (
            <div className="flex items-center justify-center py-12">
              <Loader2 size={32} className="text-cyan-400 animate-spin" />
            </div>
          )}

          {error && (
            <div className="border border-rose-400/30 bg-rose-400/5 p-3 flex items-start gap-2 text-sm">
              <AlertCircle size={16} className="text-rose-400 flex-shrink-0 mt-0.5" />
              <div className="text-rose-200">{error}</div>
            </div>
          )}

          {step === 'config' && (
            <>
              <div className="border border-cyan-400/30 bg-cyan-400/5 p-3 text-sm">
                <div className="font-bold text-cyan-400 mb-1">Configuration initiale</div>
                <div className="text-zinc-300 text-xs">Donne-moi ton Riot ID (le nom affiché en jeu), je vais récupérer tes 20 dernières ranked soloQ.</div>
              </div>

              <div className="grid grid-cols-[1fr_auto_1fr_auto] gap-2 items-end">
                <div>
                  <label className="text-[10px] text-zinc-600 font-mono">Game Name</label>
                  <input
                    type="text"
                    value={gameName}
                    onChange={(e) => setGameName(e.target.value)}
                    placeholder="Nabousan"
                    className="w-full bg-zinc-900 border border-zinc-800 text-white p-2 mt-1 focus:border-cyan-400 focus:outline-none"
                  />
                </div>
                <div className="text-2xl text-zinc-600 pb-2">#</div>
                <div>
                  <label className="text-[10px] text-zinc-600 font-mono">Tag Line</label>
                  <input
                    type="text"
                    value={tagLine}
                    onChange={(e) => setTagLine(e.target.value)}
                    placeholder="EUW"
                    className="w-full bg-zinc-900 border border-zinc-800 text-white p-2 mt-1 focus:border-cyan-400 focus:outline-none"
                  />
                </div>
                <div>
                  <label className="text-[10px] text-zinc-600 font-mono">Région</label>
                  <select
                    value={region}
                    onChange={(e) => setRegion(e.target.value)}
                    className="bg-zinc-900 border border-zinc-800 text-white p-2 mt-1 focus:border-cyan-400 focus:outline-none"
                  >
                    {REGIONS.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
                  </select>
                </div>
              </div>

              <button
                onClick={saveSettingsAndLoad}
                disabled={loadingMatches}
                className="w-full bg-gradient-to-r from-cyan-400 to-fuchsia-400 text-black p-3 font-black tracking-wider hover:opacity-90 transition disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {loadingMatches ? <><Loader2 size={14} className="animate-spin" /> CONNEXION...</> : <><Search size={14} /> CONNECTER MON COMPTE</>}
              </button>

              <div className="text-[10px] text-zinc-500 font-mono border-l-2 border-zinc-800 pl-3">
                Cette configuration est sauvegardée côté serveur dans ton compte. Tu ne la ressaisis qu'une fois.
              </div>
            </>
          )}

          {step === 'matches' && (
            <>
              {settings && (
                <div className="border border-cyan-400/30 bg-cyan-400/5 p-3 flex items-center justify-between text-sm">
                  <div>
                    <div className="font-bold text-cyan-400">{settings.riot_game_name}#{settings.riot_tag_line}</div>
                    <div className="text-[10px] text-zinc-400 font-mono">{REGIONS.find(r => r.value === settings.riot_region)?.label} · {matches.length} derniers matches ranked</div>
                  </div>
                  <button
                    onClick={() => loadMatches(settings)}
                    disabled={loadingMatches}
                    className="text-xs tracking-wider text-cyan-400 hover:text-cyan-300 border border-cyan-400/30 px-3 py-1 disabled:opacity-50"
                  >
                    {loadingMatches ? <Loader2 size={10} className="animate-spin inline" /> : 'RAFRAÎCHIR'}
                  </button>
                </div>
              )}

              {loadingMatches && matches.length === 0 && (
                <div className="flex items-center justify-center py-12">
                  <Loader2 size={32} className="text-cyan-400 animate-spin" />
                </div>
              )}

              {matches.length > 0 && (
                <div className="space-y-1 max-h-[500px] overflow-y-auto">
                  {matches.map(m => {
                    const champ = champions?.find(c => c.id === m.champion || c.name === m.champion);
                    const isImporting = importingIds.has(m.matchId);
                    return (
                      <div key={m.matchId} className={`border p-3 flex items-center gap-3 transition ${m.alreadyImported ? 'border-zinc-800 bg-zinc-900/30 opacity-60' : 'border-zinc-800 hover:border-cyan-400/30 bg-zinc-950'}`}>
                        <div className={`w-1 h-10 ${m.result === 'W' ? 'bg-gradient-to-b from-cyan-400 to-cyan-600' : 'bg-gradient-to-b from-fuchsia-500 to-rose-600'}`} />
                        <div className={`font-black text-xl w-8 ${m.result === 'W' ? 'text-cyan-400' : 'text-fuchsia-400'}`}>{m.result}</div>
                        {champ ? (
                          <img src={getChampionIconUrl(champ.id, version)} alt="" className="w-9 h-9 object-cover" loading="lazy" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                        ) : <div className="w-9 h-9 bg-zinc-800" />}
                        <div className="flex-1 min-w-0">
                          <div className="font-bold text-sm">{m.champion}</div>
                          <div className="text-[10px] text-zinc-500 font-mono">
                            {new Date(m.timestamp).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })} · {m.duration}min
                          </div>
                        </div>
                        <div className="font-mono text-sm text-right">
                          <span className="text-cyan-400">{m.kills}</span>/<span className="text-rose-400">{m.deaths}</span>/<span className="text-zinc-400">{m.assists}</span>
                        </div>
                        {m.alreadyImported ? (
                          <div className="flex items-center gap-1 text-xs text-emerald-400 font-mono">
                            <CheckCircle size={12} /> IMPORTÉE
                          </div>
                        ) : (
                          <button
                            onClick={() => importMatch(m)}
                            disabled={isImporting}
                            className="bg-gradient-to-r from-cyan-400 to-fuchsia-400 text-black px-3 py-1.5 text-xs font-bold tracking-wider hover:opacity-90 transition disabled:opacity-50 flex items-center gap-1"
                          >
                            {isImporting ? <><Loader2 size={10} className="animate-spin" /> ...</> : <><Download size={10} /> IMPORTER</>}
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}

              {!loadingMatches && matches.length === 0 && !error && (
                <div className="text-center py-8 text-zinc-500 text-sm">
                  Aucune ranked soloQ trouvée dans les matches récents.
                </div>
              )}
            </>
          )}
        </div>
    </Modal>
  );
}
